import { HTMLAttributes, useRef, useState } from "react";
import {
  ChevronRight,
  FileText,
  FolderInput,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  Shapes,
  Trash2,
} from "lucide-react";

import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/admin/_common/ui/popover";
import ConfirmDialog from "@/app/admin/_common/ConfirmDialog";
import { CHECKLIST_ICONS, checklistIcon } from "@/app/admin/_common/checklistIcons";
import { cn } from "@/app/_utils/cn";

import type { DraftCategory, DraftSection } from "../checklistDraft";
import type { DropPosition } from "../useDragReorder";
import { useSidebarDrag } from "../useSidebarDrag";
import { HIGHLIGHT_CLASSES } from "./highlight";

const rowClasses =
  "group relative mx-2 flex h-[30px] cursor-pointer select-none items-center gap-2 rounded-md pr-1 text-[13px] text-foreground hover:bg-muted";

const menuButton =
  "hidden size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-background group-hover:flex data-[state=open]:flex";

function DropLine({ position }: { position: DropPosition | null }) {
  // A real element, not ::before: preflight is off, so --tw-content is undefined here.
  if (!position) return null;
  return (
    <span
      className={cn(
        "pointer-events-none absolute inset-x-1 h-0.5 rounded-full bg-primary",
        position === "before" ? "-top-px" : "-bottom-px"
      )}
    />
  );
}

function RenameInput({
  initial,
  placeholder,
  onDone,
}: {
  initial: string;
  placeholder: string;
  onDone: (label: string | null) => void;
}) {
  const [draft, setDraft] = useState(initial);
  // Enter/Escape unmount the input, and the blur that follows must not commit a second time.
  const finished = useRef(false);
  const finish = (label: string | null) => {
    if (finished.current) return;
    finished.current = true;
    onDone(label);
  };
  return (
    <div className="mx-2 py-0.5">
      <Input
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => finish(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") finish(draft);
          if (e.key === "Escape") finish(null);
        }}
      />
    </div>
  );
}

/**
 * The category's icon slot: its icon at rest, the collapse chevron on row hover.
 * The picker opens from the row menu and anchors here.
 */
function CategoryIconSlot({
  icon,
  open,
  pickerOpen,
  onPickerOpenChange,
  onChange,
}: {
  icon: string;
  open: boolean;
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  onChange: (icon: string) => void;
}) {
  const Icon = checklistIcon(icon);
  return (
    <Popover open={pickerOpen} onOpenChange={onPickerOpenChange}>
      <PopoverTrigger asChild>
        {/* preventDefault skips Radix's own toggle; the click still bubbles to collapse the row. */}
        <span
          onClick={(e) => e.preventDefault()}
          className="flex size-6 flex-none items-center justify-center text-muted-foreground"
        >
          <Icon className="size-4 group-hover:hidden" />
          <ChevronRight className={cn("hidden size-4 transition-transform group-hover:block", open && "rotate-90")} />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[232px] p-2" onClick={(e) => e.stopPropagation()}>
        <div className="grid grid-cols-7 gap-1">
          {Object.entries(CHECKLIST_ICONS).map(([slug, Option]) => (
            <button
              key={slug}
              type="button"
              onClick={() => {
                onChange(slug);
                onPickerOpenChange(false);
              }}
              className={cn(
                "flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
                slug === icon && "bg-muted text-foreground"
              )}
              aria-label={slug}
            >
              <Option className="size-4" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SectionRow({
  section,
  categories,
  categoryUid,
  selected,
  highlighted,
  editing,
  dropPosition,
  dragging,
  dragHandlers,
  onSelect,
  onStartEdit,
  onFinishEdit,
  onMoveTo,
  onDelete,
}: {
  section: DraftSection;
  categories: DraftCategory[];
  categoryUid: string;
  selected: boolean;
  highlighted: boolean;
  editing: boolean;
  dropPosition: DropPosition | null;
  dragging: boolean;
  dragHandlers: HTMLAttributes<HTMLElement>;
  onSelect: () => void;
  onStartEdit: () => void;
  onFinishEdit: (label: string | null) => void;
  onMoveTo: (categoryUid: string) => void;
  onDelete: () => void;
}) {
  if (editing) {
    return <RenameInput initial={section.label} placeholder="Section name" onDone={onFinishEdit} />;
  }
  const others = categories.filter((c) => c.uid !== categoryUid);
  return (
    <div
      {...dragHandlers}
      onClick={onSelect}
      onDoubleClick={onStartEdit}
      className={cn(
        rowClasses,
        "pl-9",
        selected && "bg-muted",
        dragging && "opacity-40",
        highlighted && HIGHLIGHT_CLASSES
      )}
    >
      <DropLine position={dropPosition} />
      <GripVertical className="absolute left-3 hidden size-3.5 cursor-grab text-muted-foreground group-hover:block" />
      <span className={cn("min-w-0 flex-1 truncate", !section.label && "text-muted-foreground")}>
        {section.label || "Untitled section"}
      </span>
      <span className="flex-none text-[11px] tabular-nums text-muted-foreground group-hover:hidden">
        {section.fields.length}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={menuButton}
            aria-label={`More actions for ${section.label || "section"}`}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={onStartEdit}>
            <Pencil className="mr-2 size-4" />
            Rename
          </DropdownMenuItem>
          {others.length > 0 && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FolderInput className="mr-2 size-4" />
                Move to
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {others.map((c) => {
                  const Icon = checklistIcon(c.icon);
                  return (
                    <DropdownMenuItem key={c.uid} onClick={() => onMoveTo(c.uid)}>
                      <Icon className="mr-2 size-4" />
                      {c.label || "Untitled category"}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600 focus:bg-red-50 focus:text-red-700" onClick={onDelete}>
            <Trash2 className="mr-2 size-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

type Pending = { kind: "category"; item: DraftCategory } | { kind: "section"; item: DraftSection };

export default function TemplateSidebar({
  categories,
  selectedUid,
  detailsSelected,
  onSelectDetails,
  onSelectSection,
  onAddCategory,
  onAddSection,
  onRenameCategory,
  onSetCategoryIcon,
  onRemoveCategory,
  onReorderCategory,
  onRenameSection,
  onRemoveSection,
  onMoveSection,
  highlightNonceFor,
}: {
  categories: DraftCategory[];
  selectedUid: string;
  detailsSelected: boolean;
  onSelectDetails: () => void;
  onSelectSection: (uid: string) => void;
  onAddCategory: () => string;
  onAddSection: (categoryUid?: string) => string;
  onRenameCategory: (uid: string, label: string) => void;
  onSetCategoryIcon: (uid: string, icon: string) => void;
  onRemoveCategory: (uid: string) => void;
  onReorderCategory: (from: number, to: number) => void;
  onRenameSection: (uid: string, label: string) => void;
  onRemoveSection: (uid: string) => void;
  onMoveSection: (fromCategoryUid: string, fromIndex: number, toCategoryUid: string, toIndex: number) => void;
  highlightNonceFor: (uid: string) => number | null;
}) {
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<Pending | null>(null);
  const [pickingIconFor, setPickingIconFor] = useState<string | null>(null);

  const drag = useSidebarDrag({
    onReorderCategory,
    onMoveSection,
    sectionCount: (uid) => categories.find((c) => c.uid === uid)?.sections.length ?? 0,
  });

  const toggle = (uid: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const addSection = (categoryUid?: string) => {
    const uid = onAddSection(categoryUid);
    if (categoryUid) setCollapsed((current) => new Set([...current].filter((c) => c !== categoryUid)));
    setEditingUid(uid);
  };

  return (
    <aside className="flex max-h-[40vh] w-full flex-none flex-col border-b border-border bg-card md:max-h-none md:w-64 md:border-b-0 md:border-r">
      <div className="pt-2">
        <button
          type="button"
          onClick={onSelectDetails}
          className={cn(
            "mx-2 flex h-[30px] w-[calc(100%-1rem)] items-center gap-2 rounded-md px-2 text-left text-[13px] text-foreground hover:bg-muted",
            detailsSelected && "bg-muted"
          )}
        >
          <FileText className="size-4 flex-none text-muted-foreground" />
          Details
        </button>
      </div>

      <div className="flex items-center justify-between px-4 pb-1 pt-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Sections
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Add category or section"
            >
              <Plus className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditingUid(onAddCategory())}>Add category</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addSection()}>Add section</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto py-1">
        {categories.length === 0 && (
          <p className="px-4 py-2 text-[12.5px] text-muted-foreground">No categories yet</p>
        )}

        {categories.map((category, ci) => {
          const hasHighlight = category.sections.some(
            (s) => highlightNonceFor(s.uid) !== null || s.fields.some((f) => highlightNonceFor(f.uid) !== null)
          );
          const open = !collapsed.has(category.uid) || hasHighlight;
          const headerDrop = drag.categoryDrop(category.uid, ci);

          return (
            <div key={category.uid} className="flex flex-col gap-0.5">
              {editingUid === category.uid ? (
                <RenameInput
                  initial={category.label}
                  placeholder="Category name"
                  onDone={(label) => {
                    if (label !== null) onRenameCategory(category.uid, label.trim());
                    setEditingUid(null);
                  }}
                />
              ) : (
                <div
                  {...drag.categoryHandlers(category.uid, ci)}
                  onClick={() => toggle(category.uid)}
                  onDoubleClick={() => setEditingUid(category.uid)}
                  className={cn(
                    rowClasses,
                    "pl-1 font-medium",
                    headerDrop === "into" && "bg-muted ring-1 ring-inset ring-primary",
                    highlightNonceFor(category.uid) !== null && HIGHLIGHT_CLASSES
                  )}
                >
                  <DropLine position={headerDrop === "into" ? null : headerDrop} />
                  <CategoryIconSlot
                    icon={category.icon}
                    open={open}
                    pickerOpen={pickingIconFor === category.uid}
                    onPickerOpenChange={(next) => setPickingIconFor(next ? category.uid : null)}
                    onChange={(icon) => onSetCategoryIcon(category.uid, icon)}
                  />
                  <span className={cn("min-w-0 flex-1 truncate", !category.label && "text-muted-foreground")}>
                    {category.label || "Untitled category"}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className={menuButton}
                        aria-label={`More actions for ${category.label || "category"}`}
                      >
                        <MoreHorizontal className="size-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                      // Refocusing the trigger on close would dismiss the icon picker it just opened.
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <DropdownMenuItem onClick={() => addSection(category.uid)}>
                        <Plus className="mr-2 size-4" />
                        Add section
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditingUid(category.uid)}>
                        <Pencil className="mr-2 size-4" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPickingIconFor(category.uid)}>
                        <Shapes className="mr-2 size-4" />
                        Change icon
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:bg-red-50 focus:text-red-700"
                        onClick={() =>
                          category.sections.length
                            ? setPendingDelete({ kind: "category", item: category })
                            : onRemoveCategory(category.uid)
                        }
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}

              {open &&
                category.sections.map((section, si) => (
                  <SectionRow
                    key={section.uid}
                    section={section}
                    categories={categories}
                    categoryUid={category.uid}
                    selected={section.uid === selectedUid}
                    highlighted={highlightNonceFor(section.uid) !== null}
                    editing={editingUid === section.uid}
                    dropPosition={drag.sectionDrop(category.uid, si)}
                    dragging={drag.isDraggingSection(category.uid, si)}
                    dragHandlers={drag.sectionHandlers(category.uid, si)}
                    onSelect={() => onSelectSection(section.uid)}
                    onStartEdit={() => setEditingUid(section.uid)}
                    onFinishEdit={(label) => {
                      if (label !== null) onRenameSection(section.uid, label.trim());
                      setEditingUid(null);
                    }}
                    onMoveTo={(toUid) =>
                      onMoveSection(
                        category.uid,
                        si,
                        toUid,
                        categories.find((c) => c.uid === toUid)?.sections.length ?? 0
                      )
                    }
                    onDelete={() =>
                      section.fields.length
                        ? setPendingDelete({ kind: "section", item: section })
                        : onRemoveSection(section.uid)
                    }
                  />
                ))}

              {open && category.sections.length === 0 && (
                <div
                  {...drag.emptyHandlers(category.uid)}
                  className={cn(
                    "mx-2 rounded-md py-1.5 pl-9 text-[12px] text-muted-foreground",
                    drag.dragType === "section" && "border border-dashed border-border"
                  )}
                >
                  No sections
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete?.kind === "category") onRemoveCategory(pendingDelete.item.uid);
          if (pendingDelete?.kind === "section") onRemoveSection(pendingDelete.item.uid);
        }}
        title={pendingDelete?.kind === "category" ? "Delete category" : "Delete section"}
        message={
          pendingDelete?.kind === "category"
            ? `This removes ${pendingDelete.item.label || "this category"} and its ${pendingDelete.item.sections.length} sections from the template.`
            : `This removes ${pendingDelete?.item.label || "this section"} and its ${pendingDelete?.kind === "section" ? pendingDelete.item.fields.length : 0} fields from the template.`
        }
        confirmText="Delete"
      />
    </aside>
  );
}
